import React, { useEffect, useState } from 'react';
import Styles from './OperatorField.module.css';

import { Dropdown, DropdownProps, FormLabel, ProviderConsumer } from '@fluentui/react-northstar';
import { AutomationEvaluationTypes, AUTOMATION_OPERATORS, CustomPropertiesAnswerJsonKey } from '../../../../../../shared/utils/constants';
import { useTranslation } from 'react-i18next';
import { appState, AppStateValue } from '../../../../../../AppState';
import { CustomPropertyDataType_LABELS } from '../../../../../../custom-properties/utils/constants';

interface Props {
  propertyValue: string;
  operatorValue: string;
  updateOperator: (operator: string) => void;
  disabled:boolean;
  evaluationType:string;
}

export const setAutomationOperatorDataForCustomProperties = (propertyValue: string, currentState: AppStateValue, setAutomationOperatorList: React.Dispatch<React.SetStateAction<DropdownDataModel[]>>) => {
  const propId = propertyValue.replace(`${CustomPropertiesAnswerJsonKey}.`, "");
  const propData = currentState?.customProperties.find(cp => cp.PropertyId == propId);
  const propTypeName = propData?.DataType ?? "";
  switch(CustomPropertyDataType_LABELS[propTypeName]){
    case CustomPropertyDataType_LABELS.STRING:
      setAutomationOperatorList(AUTOMATION_OPERATORS['request']);
      break;
    case CustomPropertyDataType_LABELS.INT:
      setAutomationOperatorList(AUTOMATION_OPERATORS['number']);
      break;
    case CustomPropertyDataType_LABELS.BOOL:
      setAutomationOperatorList(AUTOMATION_OPERATORS['equal_notequal']);
      break;
    case CustomPropertyDataType_LABELS.LIST:
      if(propData.CustomPropertyListConfiguration?.AllowMultiple)
        setAutomationOperatorList(AUTOMATION_OPERATORS['tag']);
      else
        setAutomationOperatorList(AUTOMATION_OPERATORS['basic']);
      break;
    case CustomPropertyDataType_LABELS.DATE:
    case CustomPropertyDataType_LABELS.TIME:
      setAutomationOperatorList(AUTOMATION_OPERATORS['date']);
      break;
    default:
      setAutomationOperatorList(AUTOMATION_OPERATORS['basic']);
      break;
  }
}

export const OperatorField = ({
  propertyValue,
  operatorValue,
  updateOperator,
  disabled,
  evaluationType,
}: Props) => {
  const [automationOperatorList, setAutomationOperatorList] = useState<
    DropdownDataModel[]
  >([]);
  const { t } = useTranslation();
  const currentState = appState();

  const setAutomationOperatorData = (propertyValue: string) => {
    if(propertyValue.startsWith(`${CustomPropertiesAnswerJsonKey}.`)){
      setAutomationOperatorDataForCustomProperties(propertyValue, currentState, setAutomationOperatorList);
    }
    else if (propertyValue.toLowerCase().includes('comment')) {
      setAutomationOperatorList(AUTOMATION_OPERATORS['comment']);
    } else if (propertyValue.toLowerCase().includes('date')) {
      setAutomationOperatorList(AUTOMATION_OPERATORS['date']);
    }
    else
    {
      switch(propertyValue)
      {
        case 'title':
        case 'activephase':
        case 'ticketlifecyclephase.name':
        case 'phases.name':
        case 'note':
        case 'internalnote':
          setAutomationOperatorList(AUTOMATION_OPERATORS['stringNoEmpty']);
          break;
        case 'additionaldetails':
          setAutomationOperatorList(AUTOMATION_OPERATORS['request']);
          break;
        case 'tags':
          setAutomationOperatorList(AUTOMATION_OPERATORS['tag']);
          break;
        case 'agenttime':
          setAutomationOperatorList(AUTOMATION_OPERATORS['agentTime']);
          break;
        case 'approvers':
          setAutomationOperatorList(AUTOMATION_OPERATORS['approver']);
          break;
        case 'taskcollaborators':
        case 'ticketcollaborators':
          setAutomationOperatorList(AUTOMATION_OPERATORS['collaborators']);
          break;
        case 'affectedusers':
          setAutomationOperatorList(AUTOMATION_OPERATORS['affectedUsers']);
          break;
        case 'affecteduserscount':
        case 'collaboratorscount':
          setAutomationOperatorList(AUTOMATION_OPERATORS['AUCCount']);
          break;
        case 'requester': 
          setAutomationOperatorList(AUTOMATION_OPERATORS['requester']);
          break;
        case "status":
        case "approvalstate":
        case 'isrequiredbyall':
        case 'servicelevelagreement':
        case 'draftkb':
          setAutomationOperatorList(AUTOMATION_OPERATORS['equal_notequal']);
          break;
        case 'relatedtickets':
          setAutomationOperatorList(AUTOMATION_OPERATORS['relatedTickets']);
          break;
        case 'template':
          let operatorType = '';
          switch(evaluationType)
          {
            case AutomationEvaluationTypes.None:
              operatorType = 'templateFull';
              break;
            case AutomationEvaluationTypes.Applied:
              operatorType = 'templateChanged';
              break;
            default:
              operatorType = 'templateChangedNoneBy';
              break;
          }
          setAutomationOperatorList(AUTOMATION_OPERATORS[operatorType]);
          break;
        default:
          setAutomationOperatorList(AUTOMATION_OPERATORS['basic']);
          break;
      }
    }
    
  };

  useEffect(() => {
    setAutomationOperatorData(propertyValue);
  }, [propertyValue,evaluationType]);

  const automationOperatorChange = (e, p: DropdownProps) => {
    const data = p.value as DropdownDataModel;

    updateOperator(data?.value ?? "");
  };

  const automationOperatorSearchChange = (_e, p: DropdownProps) => {
      let data = p.searchQuery;
      if (data === null) return;
  
      const result = automationOperatorList.find(x => x.header === p.searchQuery);
      if (result){
        data = result.value;
        updateOperator(data);
      }
    };

    const getOperatorValue = () => {
      return automationOperatorList.filter(x => x.value == operatorValue);
    }

  return (
    <ProviderConsumer render={globalTheme =>
      <div className={Styles.GroupInput}>
        <FormLabel content={t('automation.automation-operators.title')} styles={{ fontSize: '12px' }} />
        <Dropdown
          key={`dropdown-searchable-true`}
          disabled={disabled}
          inverted
          fluid
          items={automationOperatorList}
          placeholder={t('automation.automation-operators.placeholder')}
          style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
          value={getOperatorValue()?.[0]}
          onChange={automationOperatorChange}
          {...{
            search: true,
            clearable: true,
            onSearchQueryChange: automationOperatorSearchChange,
            searchQuery: getOperatorValue()?.[0]?.label
          }}
        />
      </div>
    } />
  );
};
